import axios from 'axios';
import { listenDOMEvent } from '@jspartials/dom-api-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import { tryExecute } from '@jspartials/general-utils';
import {
  updateContent,
  updateSchemaContent
} from '@jsproduct/detailsPage/content';
import { updateHistory } from '@jsproduct/detailsPage/history';

// Selectors
const selector = {
  colorVariants: {
    wrapper: '.js-product-color-variants',
    list: '.js-product-color-variants__list',
    listItem: '.js-product-color-variants__list-item',
    listItemImage: '.js-product-color-variants__list-item-image',
    navigationButton: '.js-product-color-variants__navigation-button',
    navigationButtonLeft: '.js-product-color-variants__navigation-button--left',
    navigationButtonRight:
      '.js-product-color-variants__navigation-button--right'
  }
};

/**
 * Listen the color variants interactions
 * @returns {void}
 */
const listenProductColorVariants = () => {
  const colorVariantsWrapper = document.querySelector(
    selector.colorVariants.wrapper
  );

  if (colorVariantsWrapper) {
    listenColorVariantsNavigation(colorVariantsWrapper);
    observeProductColorVariants(colorVariantsWrapper);
  }
};

const observeProductColorVariants = (colorVariantsWrapper) => {
  const colorVariantsListItems = colorVariantsWrapper.querySelectorAll(
    selector.colorVariants.listItem
  );

  colorVariantsListItems.forEach((colorVariantsListItem) => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const colorVariant = entry.target;
          const productId = colorVariant?.dataset?.pid || '';
          const isCached = hasClassElement(colorVariant, classes.is.cached);

          if (productId && !isCached) {
            tryExecute(showProductColorVariant, 5, 1000, productId)
              .then((response) => {
                updateInfoProductColorVariant(colorVariant, response);
                listenColorVariantListItemClick(colorVariant);
              })
              .catch((error) => {
                console.error(error);
              });
          } else {
            listenColorVariantListItemClick(colorVariant);
          }
          observer.disconnect();
        }
      });
    });

    observer.observe(colorVariantsListItem);
  });
};

const showProductColorVariant = async (productId) => {
  try {
    const response = await axios.post(
      globalVariables.endpoint.getInfoVariantProductTile,
      {
        id: productId
      },
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response;
  } catch (error) {
    if (error.response) {
      return error.response;
    }
    throw error;
  }
};

const updateInfoProductColorVariant = (colorVariant, data) => {
  const imageElement =
    colorVariant.querySelector(selector.colorVariants.listItemImage) || null;

  // Remove the loading class
  removeClassToElement(colorVariant, classes.is.loading);

  // Update the thumbnail image
  if (imageElement) {
    imageElement.src = data.images.pdp.url;
    imageElement.alt = data.images.pdp.alt;
  }

  // Update the href attribute with the product URL
  colorVariant.href = data.url;
  colorVariant.dataset.urlVariation = data.urlVariation;
};

/**
 * Listen the click event on the color variant list item
 * @param {HTMLElement} colorVariantsListItem - The color variant list item element
 * @returns {void}
 */
const listenColorVariantListItemClick = (colorVariantsListItem) => {
  // On click
  listenDOMEvent(colorVariantsListItem, 'click', () => {
    handleColorVariantsListItemClick(colorVariantsListItem);
  });
};

const handleColorVariantsListItemClick = (colorVariantsListItem) => {
  if (!hasClassElement(colorVariantsListItem, classes.is.selected)) {
    getNewContent(colorVariantsListItem);
  }
};

const getNewContent = (colorVariantsListItem, updateHistoryEntry = true) => {
  const url = colorVariantsListItem.dataset.urlVariation;

  addClassToElement(colorVariantsListItem, classes.is.loading);

  axios
    .get(url)
    .then((response) => {
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(
        response.data.htmlContent,
        'text/html'
      );
      const htmlSchema = document
        .createRange()
        .createContextualFragment(response.data.schemaContent);
      const historyObject =
        'historyObject' in response.data ? response.data.historyObject : {};

      updateContent(htmlDoc);
      updateSchemaContent(htmlSchema);
      selectColorVariantsListItem(colorVariantsListItem);

      if (updateHistoryEntry) {
        updateHistory(historyObject);
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .then(() => {
      removeClassToElement(colorVariantsListItem, classes.is.loading);
    });
};

/**
 * Select the color variant list item
 * @param {HTMLElement} listItem - The color variant list item element
 * @returns {void}
 */
const selectColorVariantsListItem = (listItem) => {
  const list = listItem.closest(selector.colorVariants.list);
  const currentListItemSelected = list.querySelector(
    `${selector.colorVariants.listItem}.${classes.is.selected}`
  );

  if (list && currentListItemSelected) {
    removeClassToElement(currentListItemSelected, classes.is.selected);
    addClassToElement(listItem, classes.is.selected);
  }
};

/**
 * Listen the navigation on the color variants
 * @param {HTMLElement} colorVariants - The color variants element
 * @returns {void}
 */
const listenColorVariantsNavigation = (colorVariants) => {
  const colorVariantsNavigationButtons = colorVariants.querySelectorAll(
    selector.colorVariants.navigationButton
  );
  const list = colorVariants.querySelector(selector.colorVariants.list);

  listenDOMEvent(list, 'scrollend', (e) => {
    const navigationButtonLeft = colorVariants.querySelector(
      selector.colorVariants.navigationButtonLeft
    );
    const navigationButtonRight = colorVariants.querySelector(
      selector.colorVariants.navigationButtonRight
    );

    if (navigationButtonLeft && navigationButtonRight) {
      setTimeout(() => {
        if (list.scrollLeft > 0) {
          addClassToElement(navigationButtonLeft, classes.is.visible);
        }

        // Scroll left ends
        if (list.scrollLeft === 0) {
          removeClassToElement(navigationButtonLeft, classes.is.visible);
          addClassToElement(navigationButtonRight, classes.is.visible);
        }

        // Scroll right ends
        if (list.scrollLeft >= list.scrollWidth - list.clientWidth) {
          removeClassToElement(navigationButtonRight, classes.is.visible);
        } else {
          addClassToElement(navigationButtonRight, classes.is.visible);
        }
      }, 250);
    }
  });

  if (colorVariantsNavigationButtons.length > 0) {
    colorVariantsNavigationButtons.forEach((colorVariantsNavigationButton) => {
      listenDOMEvent(colorVariantsNavigationButton, 'click', () => {
        navigateColorVariants(colorVariantsNavigationButton, list);
      });
    });
  }
};

/**
 * Navigate the color variants
 * @param {HTMLElement} navigationButton - The color variants navigation button element
 * @param {HTMLElement} list - The color variants list element
 * @returns {void}
 */
const navigateColorVariants = (navigationButton, list) => {
  const direction = navigationButton.dataset.direction;
  const distance = list
    ? list.querySelector(selector.colorVariants.listItem).clientWidth * 3 +
      2 * 8
    : 0;
  const distanceToMove = direction === 'left' ? -distance : distance;

  if (list) {
    moveColorVariantsList(list, distanceToMove);
  }
};

/**
 * Move the color variants list
 * @param {HTMLElement} list - The color variants list element
 * @param {number} distance - The distance to move
 * @returns {void}
 */
const moveColorVariantsList = (list, distance) => {
  list.scrollBy({
    left: distance,
    behavior: 'smooth'
  });
};

export { listenProductColorVariants, getNewContent };
